import { useForm } from 'react-hook-form';
import CloneDeep from 'clone-deep';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import {
    Footer,
    Header,
    Meta,
    StickyCTAButtons
} from '$partials/global/index.js';

import { dispatcher as FormInputDispatcher } from '$partials/form/airtable-inputs/index.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const JobPostingPage = (props) => {
    // misc
    const page = props.data.page;
    const newsletterModule = page?.modules?.find((item) => item.__typename === 'NewsletterBlock');

    // state
    const [ fields ] = useState(props.data?.airtable?.table?.fields || {});
    const [ submitMessageFail, setSubmitMessageFail ] = useState('');
    const [ submitMessagePass, setSubmitMessagePass ] = useState('');

    // methods
    const formatFormValues = (formData) => {
        const mutatedFields = CloneDeep(formData);

        Object.entries(mutatedFields).forEach(([ id, value ]) => {
            switch (fields?.[id]?.type) {
            case 'checkbox':
            case 'date':
            case 'email':
            case 'multilineText':
            case 'multipleAttachments':
            case 'richText':
            case 'singleLineText':
            case 'singleSelect':
            case 'url':
                mutatedFields[id] = value;
                break;

            case 'currency':
                mutatedFields[id] = parseFloat(value);
                break;

            case 'multipleSelects':
                mutatedFields[id] = Array.isArray(value) ? value : [value];
                break;

            default:
                mutatedFields[id] = value;
                break;
            }
        });

        return mutatedFields;
    };

    const getBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                // Extract the Base64 part after the comma
                const base64 = reader.result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(file);
        });
    };

    // Detect content type based on file extension
    const detectContentType = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();
        switch (extension) {
        case 'pdf':
            return 'application/pdf';
        case 'jpg':
        case 'jpeg':
            return 'image/jpeg';
        case 'png':
            return 'image/png';
        default:
            return 'application/octet-stream'; // Fallback for unknown types
        }
    };

    const sendAttachment = async (fieldId, recordId, attachmentValue) => {
        const file = attachmentValue;

        if (file) {
            const fileName = file.name;
            const base64String = await getBase64(file);
            const contentType = detectContentType(fileName);

            try {
                const response = await fetch('/api/airtable/v1/addAttachment', {
                    body: JSON.stringify({
                        data: {
                            contentType: contentType,
                            file: base64String,
                            filename: fileName,
                        },
                        recordId: recordId,
                        attachmentFieldId: fieldId
                    }),
                    headers: { 'Content-type': 'application/json' },
                    method : 'POST'
                });

                const result = await response.json();

                if (result?.data?.error) {
                    throw Error(result.data.error.message);
                } else {
                    setSubmitMessagePass('Thank you for your submission!');
                }
            } catch (err) {
                setSubmitMessageFail('There was an issue with your submission, please check your form values and try again.');
                console.error(err);
            }
        }
    };

    const onSubmit = async (formData) => {
        // find multipleAttachments field ids
        const fileFieldIds = Object.entries(fields).filter(([ id ]) => fields?.[id]?.type === 'multipleAttachments').map((item) => item[0]);

        // convert values to be compatible with AirTable field types
        const mutatedFields = formatFormValues(formData);

        // create AirTable new record object structure and exclude multipleAttachments fields values
        const newRecord = {
            data: {
                records: [
                    {
                        fields: { ...mutatedFields, ...Object.fromEntries(fileFieldIds.map(id => [id, null])) }
                    }
                ]
            }
        };

        try {
            // create record
            const response = await fetch('/api/airtable/v1/setTableRecord', {
                body: JSON.stringify(newRecord),
                headers: { 'Content-type': 'application/json' },
                method : 'POST'
            });

            const result = await response.json();

            if (result?.data?.error) {
                throw Error(result.data.error.message);
            } else {
                // get all file fields with their ids and values
                const fileFields = fileFieldIds.map(id => {
                    if (mutatedFields[id] !== undefined) { // Ensure the id exists in the object
                        return { [id]: mutatedFields[id] };
                    }
                }).filter(Boolean);

                if (fileFields) {
                    // get record id of the record just created
                    const recordId = result.data.records[0].id;

                    // for each file field attach files to record
                    const requests = fileFields.map((item) => {
                        const fileFieldId = Object.entries(item)[0][0];
                        const files = Object.entries(item)[0][1];
                        Array.from(files).map(async (item, i) => {
                            return await sendAttachment(fileFieldId, recordId, files[i]);
                        });
                    });

                    // wait until all attachment requests are completed
                    await Promise.all(requests);
                }
                setSubmitMessagePass('Thank you for your submission!');
            }
        } catch (err) {
            setSubmitMessageFail('There was an issue with your submission, please check your form values and try again.');
            console.error(err);
        }
    };

    // hooks
    const {
        control,
        formState: { dirtyFields, errors },
        handleSubmit,
        register,
    } = useForm({
        defaultValues: {
            // fields according to Jobs Board > Jobs table in AirTable 2024-10-24
            fld2OvWcczJ0mTgbG: false, // Agree to T&Cs
            fld3WSB8g8aHaiVPQ: false, // Officer Mailing List Sign Up
            fldeGSRNiKTTQvckQ: '', // Annual Holiday Entitlement
            fldbsvnaT6MarureV: '', // Application Deadline
            fldgrnbaysZPaMjBu: '', // Application Link
            fldj9yPnJAzEBGj7a: '', // Authority Name
            fldilSA4QZsrsfrOv: [], // Contract Type
            fldRevfLykDb2PukE: '', // Flexible Working Options
            fldJYuSKUJDaigTlT: [], // Job Advert
            fld13PkAVnUXYZ6Co: '', // Job Location
            fldYYA7EU6fLiFPBT: '', // Job Title
            fld11w88IxSmSqYhf: '', // Pension Contribution
            fldx9oy23spbCtQ2a: '', // Salary - Maximum
            fldWT2rbOcLVZzzid: '', // Salary - Minimum
            fldE49Z008G9p9jso: '', // What is your preferred payment method?
            fldL2FujmgEmWsqNh: '', // Which Advertising Package? (null)
            fldjgvGlnxZn8LizP: [], // Which professional backgrounds would you consider suitable for this role?
            fldm09NSNwMhIFYqf: '', // Why are you recruiting for this role? (null)
            fldMOFOBZUf4t6Ve5: '', // Your email address
            fldA2RjI8HtV1EEMy: '', // Your First Name
            fldDXJSZvNOrHo4OI: '', // Your Job Title
            fldC0Cs43vuMRxAjw: '' // Your Last Name
        }
    });

    // render
    return (
        <>
            <Meta
                description={page.searchDescription || page.listingText}
                domain={props.domain}
                image={page.listingImage}
                page={page} />

            <Header
                {...props?.data?.navigation}
                {...props?.data?.ticker}
                pageType={page?.__typename}
                standfirst={page?.standfirst} />

            <StickyCTAButtons
                cta1={page?.stickyCta1?.[0]}
                cta2={page?.stickyCta2?.[0]} />

            <main id='main-content'>
                <div className='layout-container pb-100'>
                    {/* title */}
                    <div className='layout-grid-negative-gutter md:flex pb-80 md:pb-180'>
                        <div className='md:w-1/12 layout-grid-col-padding' />

                        <div className='md:w-6/12 layout-grid-col-padding'>
                            {page && (
                                <p className='type-style-4 pb-15'>
                                    Job entry
                                </p>
                            )}

                            {page?.title && (
                                <h1 className='type-style-2'>
                                    {page.title}
                                </h1>
                            )}
                        </div>
                    </div>

                    {/* information */}
                    <div className='layout-grid-negative-gutter sm:flex'>
                        <div className='hidden md:block md:w-1/12 layout-grid-col-padding' />

                        {page?.information && (
                            <>
                                <h2 className='type-style-3 pb-40 sm:w-4/12 layout-grid-col-padding'>
                                    Information
                                </h2>

                                <div
                                    className='rich-text type-style-4 sm:w-8/12 md:w-7/12 layout-grid-col-padding'
                                    dangerouslySetInnerHTML={{ __html: page.information}}/>
                            </>
                        )}
                    </div>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className='border-t border-t-charcoal' />

                    {/* ================================================================ */}

                    {/* Advertising Package */}
                    <div className='layout-container'>
                        <div className='layout-grid-negative-gutter py-60 md:py-100'>
                            <div className='sm:flex sm:flex-wrap items-start'>
                                <div className='hidden md:block md:w-1/12 layout-grid-col-padding' />

                                <h2 className='type-style-3 layout-grid-col-padding pb-40 md:pb-0 sm:w-4/12'>
                                    Advertising Package
                                </h2>

                                {/* Which Advertising Package? (required) */}
                                <div className='sm:w-8/12 md:w-6/12 -mt-30'>
                                    <div className='layout-grid-col-padding'>
                                        {fields?.['fldL2FujmgEmWsqNh'] && (
                                            <FormInputDispatcher
                                                { ...fields['fldL2FujmgEmWsqNh'] }
                                                control={control}
                                                errors={errors?.['fldL2FujmgEmWsqNh']}
                                                isDirty={Boolean(dirtyFields?.['fldL2FujmgEmWsqNh'])}
                                                register={register}
                                                required={true} />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* What is your preferred payment method? (required) */}
                            <div className='sm:flex sm:flex-wrap items-start'>
                                <div className='hidden sm:block sm:w-4/12 md:w-5/12 layout-grid-col-padding' />

                                <div className='sm:w-8/12 md:w-6/12'>
                                    <div className='layout-grid-col-padding'>
                                        {fields?.['fldE49Z008G9p9jso'] && (
                                            <FormInputDispatcher
                                                { ...fields['fldE49Z008G9p9jso'] }
                                                control={control}
                                                errors={errors?.['fldE49Z008G9p9jso']}
                                                isDirty={Boolean(dirtyFields?.['fldE49Z008G9p9jso'])}
                                                register={register}
                                                required={true} />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Agree to T&Cs (required) */}
                            <div className='sm:flex sm:flex-wrap items-start'>
                                <div className='hidden sm:block sm:w-4/12 md:w-5/12 layout-grid-col-padding' />

                                <div className='sm:w-8/12 md:w-6/12'>
                                    <div className='layout-grid-col-padding'>
                                        {fields?.['fld2OvWcczJ0mTgbG'] && (
                                            <FormInputDispatcher
                                                { ...fields['fld2OvWcczJ0mTgbG'] }
                                                control={control}
                                                errors={errors?.['fld2OvWcczJ0mTgbG']}
                                                isDirty={Boolean(dirtyFields?.['fld2OvWcczJ0mTgbG'])}
                                                register={register}
                                                required={true} />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Officer Mailing List */}
                            <div className='sm:flex sm:flex-wrap items-start'>
                                <div className='hidden sm:block sm:w-4/12 md:w-5/12 layout-grid-col-padding' />

                                <div className='sm:w-8/12 md:w-6/12'>
                                    <div className='layout-grid-col-padding'>
                                        {fields?.['fld3WSB8g8aHaiVPQ'] && (
                                            <FormInputDispatcher
                                                { ...fields['fld3WSB8g8aHaiVPQ'] }
                                                control={control}
                                                errors={errors?.['fld3WSB8g8aHaiVPQ']}
                                                isDirty={Boolean(dirtyFields?.['fld3WSB8g8aHaiVPQ'])}
                                                register={register}
                                                required={false} />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='border-t border-t-charcoal' />

                    {/* ================================================================ */}

                    {/* About You */}
                    <div className='layout-container'>
                        <div className='layout-grid-negative-gutter py-60 md:py-100'>
                            <div className='sm:flex sm:flex-wrap items-start'>
                                <div className='hidden md:block md:w-1/12 layout-grid-col-padding' />

                                <h2 className='type-style-3 layout-grid-col-padding pb-40 md:pb-0 sm:w-4/12'>
                                    About You
                                </h2>

                                {/* Your First Name (required) */}
                                <div className='sm:w-8/12 md:w-6/12 -mt-30'>
                                    <div className='layout-grid-col-padding'>
                                        {fields?.['fldA2RjI8HtV1EEMy'] && (
                                            <FormInputDispatcher
                                                { ...fields['fldA2RjI8HtV1EEMy'] }
                                                control={control}
                                                errors={errors?.['fldA2RjI8HtV1EEMy']}
                                                isDirty={Boolean(dirtyFields?.['fldA2RjI8HtV1EEMy'])}
                                                register={register}
                                                required={true} />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Your Last Name (required) */}
                            <div className='sm:flex sm:flex-wrap items-start'>
                                <div className='hidden sm:block sm:w-4/12 md:w-5/12 layout-grid-col-padding' />

                                <div className='sm:w-8/12 md:w-6/12'>
                                    <div className='layout-grid-col-padding'>
                                        {fields?.['fldC0Cs43vuMRxAjw'] && (
                                            <FormInputDispatcher
                                                { ...fields['fldC0Cs43vuMRxAjw'] }
                                                control={control}
                                                errors={errors?.['fldC0Cs43vuMRxAjw']}
                                                isDirty={Boolean(dirtyFields?.['fldC0Cs43vuMRxAjw'])}
                                                register={register}
                                                required={true} />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Authority Name (required) */}
                            <div className='sm:flex sm:flex-wrap items-start'>
                                <div className='hidden sm:block sm:w-4/12 md:w-5/12 layout-grid-col-padding' />

                                <div className='sm:w-8/12 md:w-6/12'>
                                    <div className='layout-grid-col-padding'>
                                        {fields?.['fldj9yPnJAzEBGj7a'] && (
                                            <FormInputDispatcher
                                                { ...fields['fldj9yPnJAzEBGj7a'] }
                                                control={control}
                                                errors={errors?.['fldj9yPnJAzEBGj7a']}
                                                isDirty={Boolean(dirtyFields?.['fldj9yPnJAzEBGj7a'])}
                                                register={register}
                                                required={true} />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Your Job Title (required) */}
                            <div className='sm:flex sm:flex-wrap items-start'>
                                <div className='hidden sm:block sm:w-4/12 md:w-5/12 layout-grid-col-padding' />

                                <div className='sm:w-8/12 md:w-6/12'>
                                    <div className='layout-grid-col-padding'>
                                        {fields?.['fldDXJSZvNOrHo4OI'] && (
                                            <FormInputDispatcher
                                                { ...fields['fldDXJSZvNOrHo4OI'] }
                                                control={control}
                                                errors={errors?.['fldDXJSZvNOrHo4OI']}
                                                isDirty={Boolean(dirtyFields?.['fldDXJSZvNOrHo4OI'])}
                                                register={register}
                                                required={true} />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Your email address (required) */}
                            <div className='sm:flex sm:flex-wrap items-start'>
                                <div className='hidden sm:block sm:w-4/12 md:w-5/12 layout-grid-col-padding' />

                                <div className='sm:w-8/12 md:w-6/12'>
                                    <div className='layout-grid-col-padding'>
                                        {fields?.['fldMOFOBZUf4t6Ve5'] && (
                                            <FormInputDispatcher
                                                { ...fields['fldMOFOBZUf4t6Ve5'] }
                                                control={control}
                                                errors={errors?.['fldMOFOBZUf4t6Ve5']}
                                                isDirty={Boolean(dirtyFields?.['fldMOFOBZUf4t6Ve5'])}
                                                register={register}
                                                required={true} />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='border-t border-t-charcoal' />

                    {/* ================================================================ */}

                    {/* Job Basics */}
                    <div className='layout-container'>
                        <div className='layout-grid-negative-gutter py-60 md:py-100'>
                            <div className='sm:flex sm:flex-wrap items-start'>
                                <div className='hidden md:block md:w-1/12 layout-grid-col-padding' />

                                <h2 className='type-style-3 layout-grid-col-padding pb-40 md:pb-0 sm:w-4/12'>
                                    Job Basics
                                </h2>

                                {/* Job Title (required) */}
                                <div className='sm:w-8/12 md:w-6/12 -mt-30'>
                                    <div className='layout-grid-col-padding'>
                                        {fields?.['fldYYA7EU6fLiFPBT'] && (
                                            <FormInputDispatcher
                                                { ...fields['fldYYA7EU6fLiFPBT'] }
                                                control={control}
                                                errors={errors?.['fldYYA7EU6fLiFPBT']}
                                                isDirty={Boolean(dirtyFields?.['fldYYA7EU6fLiFPBT'])}
                                                register={register}
                                                required={true} />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Application Deadline (required) */}
                            <div className='sm:flex sm:flex-wrap items-start'>
                                <div className='hidden sm:block sm:w-4/12 md:w-5/12 layout-grid-col-padding' />

                                <div className='sm:w-8/12 md:w-6/12'>
                                    <div className='layout-grid-col-padding'>
                                        {fields?.['fldbsvnaT6MarureV'] && (
                                            <FormInputDispatcher
                                                { ...fields['fldbsvnaT6MarureV'] }
                                                control={control}
                                                errors={errors?.['fldbsvnaT6MarureV']}
                                                isDirty={Boolean(dirtyFields?.['fldbsvnaT6MarureV'])}
                                                register={register}
                                                required={true} />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Job Posting URL (required) */}
                            <div className='sm:flex sm:flex-wrap items-start'>
                                <div className='hidden sm:block sm:w-4/12 md:w-5/12 layout-grid-col-padding' />

                                <div className='sm:w-8/12 md:w-6/12'>
                                    <div className='layout-grid-col-padding'>
                                        {fields?.['fldgrnbaysZPaMjBu'] && (
                                            <FormInputDispatcher
                                                { ...fields['fldgrnbaysZPaMjBu'] }
                                                control={control}
                                                errors={errors?.['fldgrnbaysZPaMjBu']}
                                                isDirty={Boolean(dirtyFields?.['fldgrnbaysZPaMjBu'])}
                                                register={register}
                                                required={true} />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Job Advert */}
                            <div className='sm:flex sm:flex-wrap items-start'>
                                <div className='hidden sm:block sm:w-4/12 md:w-5/12 layout-grid-col-padding' />

                                <div className='sm:w-8/12 md:w-6/12'>
                                    <div className='layout-grid-col-padding'>
                                        {fields?.['fldJYuSKUJDaigTlT'] && (
                                            <FormInputDispatcher
                                                { ...fields['fldJYuSKUJDaigTlT'] }
                                                control={control}
                                                errors={errors?.['fldJYuSKUJDaigTlT']}
                                                isDirty={Boolean(dirtyFields?.['fldJYuSKUJDaigTlT'])}
                                                register={register}
                                                required={false} />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Why are you recruiting for this role? (required) */}
                            <div className='sm:flex sm:flex-wrap items-start'>
                                <div className='hidden sm:block sm:w-4/12 md:w-5/12 layout-grid-col-padding' />

                                <div className='sm:w-8/12 md:w-6/12'>
                                    <div className='layout-grid-col-padding'>
                                        {fields?.['fldm09NSNwMhIFYqf'] && (
                                            <FormInputDispatcher
                                                { ...fields['fldm09NSNwMhIFYqf'] }
                                                control={control}
                                                errors={errors?.['fldm09NSNwMhIFYqf']}
                                                isDirty={Boolean(dirtyFields?.['fldm09NSNwMhIFYqf'])}
                                                register={register}
                                                required={true} />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Contract Type (required) */}
                            <div className='sm:flex sm:flex-wrap items-start'>
                                <div className='hidden sm:block sm:w-4/12 md:w-5/12 layout-grid-col-padding' />

                                <div className='sm:w-8/12 md:w-6/12'>
                                    <div className='layout-grid-col-padding'>
                                        {fields?.['fldilSA4QZsrsfrOv'] && (
                                            <FormInputDispatcher
                                                { ...fields['fldilSA4QZsrsfrOv'] }
                                                control={control}
                                                errors={errors?.['fldilSA4QZsrsfrOv']}
                                                isDirty={Boolean(dirtyFields?.['fldilSA4QZsrsfrOv'])}
                                                register={register}
                                                required={true} />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Job Location (required) */}
                            <div className='sm:flex sm:flex-wrap items-start'>
                                <div className='hidden sm:block sm:w-4/12 md:w-5/12 layout-grid-col-padding' />

                                <div className='sm:w-8/12 md:w-6/12'>
                                    <div className='layout-grid-col-padding'>
                                        {fields?.['fld13PkAVnUXYZ6Co'] && (
                                            <FormInputDispatcher
                                                { ...fields['fld13PkAVnUXYZ6Co'] }
                                                control={control}
                                                errors={errors?.['fld13PkAVnUXYZ6Co']}
                                                isDirty={Boolean(dirtyFields?.['fld13PkAVnUXYZ6Co'])}
                                                register={register}
                                                required={true} />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Flexible Working Options (required) */}
                            <div className='sm:flex sm:flex-wrap items-start'>
                                <div className='hidden sm:block sm:w-4/12 md:w-5/12 layout-grid-col-padding' />

                                <div className='sm:w-8/12 md:w-6/12'>
                                    <div className='layout-grid-col-padding'>
                                        {fields?.['fldRevfLykDb2PukE'] && (
                                            <FormInputDispatcher
                                                { ...fields['fldRevfLykDb2PukE'] }
                                                control={control}
                                                errors={errors?.['fldRevfLykDb2PukE']}
                                                isDirty={Boolean(dirtyFields?.['fldRevfLykDb2PukE'])}
                                                register={register}
                                                required={true} />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Which professional backgrounds would you consider suitable for this role? (required) */}
                            <div className='sm:flex sm:flex-wrap items-start'>
                                <div className='hidden sm:block sm:w-4/12 md:w-5/12 layout-grid-col-padding' />

                                <div className='sm:w-8/12 md:w-6/12'>
                                    <div className='layout-grid-col-padding'>
                                        {fields?.['fldjgvGlnxZn8LizP'] && (
                                            <FormInputDispatcher
                                                { ...fields['fldjgvGlnxZn8LizP'] }
                                                control={control}
                                                errors={errors?.['fldjgvGlnxZn8LizP']}
                                                isDirty={Boolean(dirtyFields?.['fldjgvGlnxZn8LizP'])}
                                                register={register}
                                                required={true} />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='border-t border-t-charcoal' />

                    {/* ================================================================ */}

                    {/* Job Salary and Benefits */}
                    <div className='layout-container'>
                        <div className='layout-grid-negative-gutter py-60 md:py-100'>
                            <div className='sm:flex sm:flex-wrap items-start'>
                                <div className='hidden md:block md:w-1/12 layout-grid-col-padding' />

                                <h2 className='type-style-3 layout-grid-col-padding pb-40 md:pb-0 sm:w-4/12'>
                                    Job Salary and Benefits
                                </h2>

                                {/* Salary - Minimum (required) */}
                                <div className='sm:w-8/12 md:w-6/12 -mt-30'>
                                    <div className='layout-grid-col-padding'>
                                        {fields?.['fldWT2rbOcLVZzzid'] && (
                                            <FormInputDispatcher
                                                { ...fields['fldWT2rbOcLVZzzid'] }
                                                control={control}
                                                errors={errors?.['fldWT2rbOcLVZzzid']}
                                                isDirty={Boolean(dirtyFields?.['fldWT2rbOcLVZzzid'])}
                                                register={register}
                                                required={true} />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Salary - Maximum (required) */}
                            <div className='sm:flex sm:flex-wrap items-start'>
                                <div className='hidden sm:block sm:w-4/12 md:w-5/12 layout-grid-col-padding' />

                                <div className='sm:w-8/12 md:w-6/12'>
                                    <div className='layout-grid-col-padding'>
                                        {fields?.['fldx9oy23spbCtQ2a'] && (
                                            <FormInputDispatcher
                                                { ...fields['fldx9oy23spbCtQ2a'] }
                                                control={control}
                                                errors={errors?.['fldx9oy23spbCtQ2a']}
                                                isDirty={Boolean(dirtyFields?.['fldx9oy23spbCtQ2a'])}
                                                register={register}
                                                required={false} />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Pension Contribution (required) */}
                            <div className='sm:flex sm:flex-wrap items-start'>
                                <div className='hidden sm:block sm:w-4/12 md:w-5/12 layout-grid-col-padding' />

                                <div className='sm:w-8/12 md:w-6/12'>
                                    <div className='layout-grid-col-padding'>
                                        {fields?.['fld11w88IxSmSqYhf'] && (
                                            <FormInputDispatcher
                                                { ...fields['fld11w88IxSmSqYhf'] }
                                                control={control}
                                                errors={errors?.['fld11w88IxSmSqYhf']}
                                                isDirty={Boolean(dirtyFields?.['fld11w88IxSmSqYhf'])}
                                                register={register}
                                                required={true} />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Annual Holiday Entitlement (required) */}
                            <div className='sm:flex sm:flex-wrap items-start'>
                                <div className='hidden sm:block sm:w-4/12 md:w-5/12 layout-grid-col-padding' />

                                <div className='sm:w-8/12 md:w-6/12'>
                                    <div className='layout-grid-col-padding'>
                                        {fields?.['fldeGSRNiKTTQvckQ'] && (
                                            <FormInputDispatcher
                                                { ...fields['fldeGSRNiKTTQvckQ'] }
                                                control={control}
                                                errors={errors?.['fldeGSRNiKTTQvckQ']}
                                                isDirty={Boolean(dirtyFields?.['fldeGSRNiKTTQvckQ'])}
                                                register={register}
                                                required={true} />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='border-t border-t-charcoal' />

                    {/* ================================================================ */}

                    {/* Submit */}
                    <div className='layout-container'>
                        <div className='layout-grid-negative-gutter py-60 md:py-100'>
                            <div className='sm:flex items-start'>
                                <div className='hidden sm:block sm:w-4/12 md:w-5/12 layout-grid-col-padding' />

                                <div className='sm:w-8/12 md:w-6/12'>
                                    <div className='layout-grid-col-padding'>
                                        <div className='bg-peach px-20 py-30'>
                                            <h2 className='type-style-3 pb-30'>
                                                {page?.submissionTitle}
                                            </h2>

                                            <p className='type-style-4 pb-30'>
                                                {page?.submissionBody}
                                            </p>

                                            {submitMessagePass ? (
                                                <p className='type-style-5'>
                                                    {submitMessagePass}
                                                </p>
                                            ) : (
                                                <button
                                                    className='pill-button'
                                                    type='submit'>
                                                    {page?.submissionButtonLabel}
                                                </button>
                                            )}

                                            {submitMessageFail && (
                                                <p className='type-style-5 mt-10'>
                                                    {submitMessageFail}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </main>

            <Footer
                hasNewsletterModule={newsletterModule}
                {...props?.data?.footer}
                {...props?.data?.newsletter} />
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    data: PropTypes.shape({
        airtable: PropTypes.object.isRequired,
        footer: PropTypes.object,
        navigation: PropTypes.object,
        newsletter: PropTypes.object,
        page: PropTypes.object.isRequired,
        ticker: PropTypes.object,
    }),
    domain: PropTypes.string
};

JobPostingPage.propTypes = propTypes;

export default JobPostingPage;
